import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const EMPTY = { country: "United States", state: "Florida", metro: "Tampa Bay", city: "", zip_codes: [], active: true };

export default function Markets() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [zips, setZips] = useState("");
    const { data } = useQuery({ queryKey: ["admin-markets"], queryFn: async () => (await api.get("/admin/markets")).data });
    const [mq, setMq] = useState("");
    const [sort, setSort] = useState({ key: "city", dir: "asc" });
    const toggleSort = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
    const SortHead = ({ k, children }) => (
        <TableHead><button type="button" data-testid={`market-sort-${k}`} onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 font-600 hover:text-zinc-900">{children}{sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}</button></TableHead>
    );
    const rows = useMemo(() => {
        let arr = [...(data || [])];
        if (mq) { const s = mq.toLowerCase(); arr = arr.filter((m) => [m.country, m.state, m.metro, m.city].some((x) => (x || "").toLowerCase().includes(s))); }
        const val = (m) => (sort.key === "active" ? (m.active ? "active" : "inactive") : (m[sort.key] || "")).toString().toLowerCase();
        arr.sort((a, b) => { const av = val(a), bv = val(b); if (av < bv) return sort.dir === "asc" ? -1 : 1; if (av > bv) return sort.dir === "asc" ? 1 : -1; return 0; });
        return arr;
    }, [data, mq, sort]);

    const save = async () => {
        try {
            await api.post("/admin/markets", { ...form, zip_codes: zips.split(",").map((z) => z.trim()).filter(Boolean) });
            toast.success("Market added"); setOpen(false); setForm(EMPTY); setZips(""); qc.invalidateQueries({ queryKey: ["admin-markets"] });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const del = async (id) => { await api.delete(`/admin/markets/${id}`); qc.invalidateQueries({ queryKey: ["admin-markets"] }); };

    return (
        <div>
            <div className="flex items-center justify-between">
                <AdminHeader title="Markets" subtitle="Cities and metros the club operates in. The architecture supports nationwide expansion." />
                <Button onClick={() => setOpen(true)} data-testid="market-new"><Plus className="mr-1 h-4 w-4" /> New market</Button>
            </div>
            <div className="mb-3 flex max-w-sm items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
                <Search className="h-4 w-4 text-zinc-400" />
                <input data-testid="market-search" value={mq} onChange={(e) => setMq(e.target.value)} placeholder="Find by city, state, or metro" className="w-full bg-transparent text-sm outline-none" />
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
                <Table>
                    <TableHeader><TableRow><SortHead k="country">Country</SortHead><SortHead k="state">State</SortHead><SortHead k="metro">Metro</SortHead><SortHead k="city">City</SortHead><SortHead k="active">Status</SortHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                        {rows.map((m) => (
                            <TableRow key={m.id} data-testid={`market-row-${m.id}`}>
                                <TableCell>{m.country}</TableCell><TableCell>{m.state}</TableCell><TableCell>{m.metro}</TableCell>
                                <TableCell className="font-500">{m.city}</TableCell>
                                <TableCell><Badge tone={m.active ? "green" : "gray"}>{m.active ? "active" : "inactive"}</Badge></TableCell>
                                <TableCell><button onClick={() => del(m.id)} data-testid={`market-del-${m.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>New market</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-sm font-600">Country</label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
                        <div><label className="text-sm font-600">State</label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1" /></div>
                        <div><label className="text-sm font-600">Metro</label><Input value={form.metro} onChange={(e) => setForm({ ...form, metro: e.target.value })} className="mt-1" /></div>
                        <div><label className="text-sm font-600">City</label><Input data-testid="market-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
                        <div className="col-span-2"><label className="text-sm font-600">ZIP codes (comma separated)</label><Input value={zips} onChange={(e) => setZips(e.target.value)} className="mt-1" /></div>
                    </div>
                    <DialogFooter><Button onClick={save} data-testid="market-save">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
